import firebase from "firebase/app";
import "firebase/firestore";

const COLLECTION = "mtp_launch_notices";
const SETTINGS_DOC = "mtp_launch";

function mapLaunchSettings(data) {
  return {
    isShown: !!data.isShown,
    expiresAt: data.expiresAt ? data.expiresAt.toMillis() : null,
  };
}

function fetchMtpLaunchSettings(db) {
  return db.collection("settings").doc(SETTINGS_DOC).get()
    .then((doc) => {
      if (!doc.exists) return { isShown: false, expiresAt: null };
      return mapLaunchSettings(doc.data() || {});
    })
    .catch((error) => {
      console.error("Error getting mtp_launch settings:", error);
      return { isShown: false, expiresAt: null };
    });
}

function subscribeMtpLaunchSettings(db, onUpdate, onError) {
  return db.collection("settings").doc(SETTINGS_DOC).onSnapshot(
    (snapshot) => {
      if (!snapshot.exists) {
        onUpdate({ isShown: false, expiresAt: null });
        return;
      }
      onUpdate(mapLaunchSettings(snapshot.data() || {}));
    },
    (error) => {
      console.error("Error listening to mtp_launch settings:", error);
      if (onError) onError(error);
    }
  );
}

function fetchMtpLaunchNotice(db, uid) {
  return db.collection(COLLECTION).doc(uid).get()
    .then((doc) => (doc.exists ? doc.data() : null))
    .catch((error) => {
      console.error("Error getting mtp_launch_notices document:", error);
      return null;
    });
}

/**
 * Banner permanently hidden only for dismissed (Don't show…) or legacy opened
 * (older builds that hid on Start Free Mock Exam — leave those as-is).
 */
function isMtpLaunchNoticeAnswered(notice) {
  return !!(notice && notice.status && notice.status !== "viewed");
}

/**
 * Record a banner open (Test Your Readiness). Creates the doc on first open,
 * then increments bannerOpenCount on later opens while status is still viewed.
 */
function recordMtpLaunchView(db, { uid, email }) {
  const ref = db.collection(COLLECTION).doc(uid);
  return db.runTransaction((transaction) => {
    return transaction.get(ref).then((doc) => {
      if (!doc.exists) {
        transaction.set(ref, {
          uid,
          email: email || null,
          status: "viewed",
          bannerOpenCount: 1,
          startFreeMockCount: 0,
          dontShowAgainCount: 0,
          viewedAt: firebase.firestore.FieldValue.serverTimestamp(),
          userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
        });
        return;
      }
      const data = doc.data() || {};
      if (data.status && data.status !== "viewed") return;
      transaction.update(ref, {
        email: email || data.email || null,
        bannerOpenCount: firebase.firestore.FieldValue.increment(1),
        userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
      });
    });
  }).catch((error) => {
    console.error("Error recording MTP launch view:", error);
  });
}

/**
 * Start Free Mock Exam: increment counter only; keep status viewed so the banner stays.
 */
function recordMtpLaunchStartFreeMock(db, { uid, email }) {
  const ref = db.collection(COLLECTION).doc(uid);
  return db.runTransaction((transaction) => {
    return transaction.get(ref).then((doc) => {
      if (!doc.exists) {
        transaction.set(ref, {
          uid,
          email: email || null,
          status: "viewed",
          bannerOpenCount: 0,
          startFreeMockCount: 1,
          dontShowAgainCount: 0,
          viewedAt: firebase.firestore.FieldValue.serverTimestamp(),
          userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
        });
        return;
      }
      const data = doc.data() || {};
      if (data.status && data.status !== "viewed") return;
      transaction.update(ref, {
        email: email || data.email || null,
        startFreeMockCount: firebase.firestore.FieldValue.increment(1),
        userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
      });
    });
  }).catch((error) => {
    console.error("Error recording MTP Start Free Mock Exam:", error);
    throw error;
  });
}

/**
 * Don't show this banner again — permanent dismiss only.
 */
function recordMtpLaunchResponse(db, { uid, email, status }) {
  if (status !== "dismissed") {
    return Promise.reject(new Error(`Unsupported MTP launch response status: ${status}`));
  }
  const ref = db.collection(COLLECTION).doc(uid);
  return db.runTransaction((transaction) => {
    return transaction.get(ref).then((doc) => {
      if (doc.exists) {
        const data = doc.data() || {};
        if (data.status && data.status !== "viewed") return;
        transaction.update(ref, {
          email: email || data.email || null,
          status: "dismissed",
          dontShowAgainCount: firebase.firestore.FieldValue.increment(1),
          respondedAt: firebase.firestore.FieldValue.serverTimestamp(),
          userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
        });
        return;
      }
      transaction.set(ref, {
        uid,
        email: email || null,
        status: "dismissed",
        bannerOpenCount: 0,
        startFreeMockCount: 0,
        dontShowAgainCount: 1,
        viewedAt: firebase.firestore.FieldValue.serverTimestamp(),
        respondedAt: firebase.firestore.FieldValue.serverTimestamp(),
        userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
      });
    });
  }).catch((error) => {
    console.error("Error recording MTP launch response:", error);
    throw error;
  });
}

export const mtpLaunchNoticeService = {
  fetchMtpLaunchSettings,
  subscribeMtpLaunchSettings,
  fetchMtpLaunchNotice,
  isMtpLaunchNoticeAnswered,
  recordMtpLaunchView,
  recordMtpLaunchStartFreeMock,
  recordMtpLaunchResponse,
};
